import React, { useCallback } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, useParams, useSearchParams } from 'react-router-dom'
import { api, resolveApiAssetUrl } from '../lib/api'
import { categoryLandingOgShareUrl } from '../lib/publicShareUrls'
import Loading from '../ui/Loading'
import InlineError from '../ui/InlineError'
import ShareLinkActions from '../components/ShareLinkActions'
import CategoryLandingEditorModeToggle from '../components/categoryLanding/CategoryLandingEditorModeToggle'
import CategoryLandingJsonEditorCard from '../components/categoryLanding/CategoryLandingJsonEditorCard'
import CategoryLandingSectionBlockEditor from '../components/categoryLanding/CategoryLandingSectionBlockEditor'
import CategoryLandingFieldListEditor from '../components/categoryLanding/CategoryLandingFieldListEditor'
import CategoryLandingLayoutColorField, { isCategoryLandingLayoutColorKey } from '../components/categoryLanding/CategoryLandingLayoutColorField'
import useCategoryLandingEditorState, { prettyJsonValue } from '../hooks/useCategoryLandingEditorState'
import {
  applyCategoryLandingLayout,
  createCategoryLandingDefaultFormFields,
  getCategoryLandingLayoutEditableFields,
  getCategoryLandingLayoutOptions,
  getCodeTemplatePresetHint,
  normalizeCategoryLandingLayoutConfig,
} from '../templates/categoryLanding/registry'
import { getCategoryLabel } from '../templates/categoryLanding/common/categories'
import { getCategoryLandingHeroImage, getCategoryLandingHeroImageOptions } from '../templates/categoryLanding/common/heroImages'
import CategoryLandingSaveReviewModal from '../components/CategoryLandingSaveReviewModal'
import { collectMarketingStrings } from '../lib/collectMarketingStrings'
import EasyReconfigureModal from '../features/reconfigure/EasyReconfigureModal'
import {
  CATEGORY_LANDING_RECONFIGURE_FEATURES,
  CATEGORY_LANDING_RECONFIGURE_PURPOSES,
  buildCategoryLandingReconfigureState,
  getCategoryPurposeFromTemplate,
  getDefaultCategoryLandingFeatureValues,
} from '../features/reconfigure/reconfigurePresets'

function parseJsonObject(raw, fallback = {}) {
  if (!raw) return fallback
  if (typeof raw === 'object' && !Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function parseJsonArray(raw, fallback = []) {
  if (!raw) return fallback
  if (Array.isArray(raw)) return raw
  try {
    const parsed = JSON.parse(String(raw))
    return Array.isArray(parsed) ? parsed : fallback
  } catch {
    return fallback
  }
}

function coerceLayoutFieldValue(field, value) {
  if (field?.type === 'number') return Number(value || 0)
  return value
}

function TemplateThumb({ src, alt, className = '' }) {
  if (!src) {
    return (
      <div className={`flex items-center justify-center rounded-xl border border-dashed border-slate-200 bg-slate-50 text-xs text-slate-400 ${className}`}>
        미리보기 없음
      </div>
    )
  }
  return <img src={resolveApiAssetUrl(src)} alt={alt} className={`rounded-xl object-cover ${className}`} />
}

const CATEGORY_LANDING_WRITE_DESCRIPTIONS = {
  hero: '첫 화면 제목과 설명을 작성합니다.',
  contentSections: '상품/서비스 설명 섹션의 문구와 이미지를 정리합니다.',
  formEnabled: '상담 신청 폼 제목과 받을 항목을 확인합니다.',
  surveyEnabled: '상담 전 설문 문항을 정리합니다.',
  adReviewNotice: '저장할 때 광고 심의필 문구를 확인합니다.',
}

function getCategoryLandingWritingItems(next = {}) {
  const items = [
    {
      id: 'hero',
      label: '상단 제목·설명',
      description: CATEGORY_LANDING_WRITE_DESCRIPTIONS.hero,
    },
  ]

  if ((next.contentSections || []).length > 0) {
    items.push({
      id: 'contentSections',
      label: '상품/서비스 설명',
      description: CATEGORY_LANDING_WRITE_DESCRIPTIONS.contentSections,
    })
  }

  if (next.formEnabled) {
    items.push({
      id: 'formEnabled',
      label: '상담 신청 폼',
      description: CATEGORY_LANDING_WRITE_DESCRIPTIONS.formEnabled,
    })
  }

  if (next.surveyEnabled) {
    items.push({
      id: 'surveyEnabled',
      label: '설문 문항',
      description: CATEGORY_LANDING_WRITE_DESCRIPTIONS.surveyEnabled,
    })
  }

  items.push({
    id: 'adReviewNotice',
    label: '심의필 문구',
    description: CATEGORY_LANDING_WRITE_DESCRIPTIONS.adReviewNotice,
  })

  return items
}

export default function CategoryLandingPageEditPage() {
  const { id } = useParams()
  const [searchParams, setSearchParams] = useSearchParams()
  const autoReconfigureOpenedRef = React.useRef(false)
  const [heroTitle, setHeroTitle] = React.useState('')
  const [heroSubtitle, setHeroSubtitle] = React.useState('')
  const [heroImageFileId, setHeroImageFileId] = React.useState('')
  const [heroVideoFileId, setHeroVideoFileId] = React.useState('')
  const [contentSections, setContentSections] = React.useState([])
  const [formEnabled, setFormEnabled] = React.useState(true)
  const [formTitle, setFormTitle] = React.useState('상담 신청')
  const [formFields, setFormFields] = React.useState(createCategoryLandingDefaultFormFields({ includeMessage: true }))
  const [surveyEnabled, setSurveyEnabled] = React.useState(true)
  const [surveyCustomFields, setSurveyCustomFields] = React.useState([])
  const [customConfigObj, setCustomConfigObj] = React.useState({})
  const [enabled, setEnabled] = React.useState(false)
  const [kpiRetentionRate, setKpiRetentionRate] = React.useState('')
  const [kpiConsultCount, setKpiConsultCount] = React.useState('')
  const [kpiResponseRate, setKpiResponseRate] = React.useState('')
  const [kpiSatisfaction, setKpiSatisfaction] = React.useState('')
  const [uploadingTarget, setUploadingTarget] = React.useState('')
  const [selectedTemplateId, setSelectedTemplateId] = React.useState('')
  const [overwriteTemplate, setOverwriteTemplate] = React.useState(false)
  const [editMode, setEditMode] = React.useState('standard') // 'standard' or 'advanced'
  const [advancedEditorMode, setAdvancedEditorMode] = React.useState('block')
  const [contentSectionsJson, setContentSectionsJson] = React.useState('[]')
  const [formFieldsJson, setFormFieldsJson] = React.useState('[]')
  const [surveyCustomFieldsJson, setSurveyCustomFieldsJson] = React.useState('[]')
  const [customConfigJson, setCustomConfigJson] = React.useState('{}')
  const [adReviewNotice, setAdReviewNotice] = React.useState('')
  const [saveReviewOpen, setSaveReviewOpen] = React.useState(false)
  const [saveReviewNoticeDraft, setSaveReviewNoticeDraft] = React.useState('')
  const [reconfigureOpen, setReconfigureOpen] = React.useState(false)
  const [reconfigurePurpose, setReconfigurePurpose] = React.useState('quick')
  const [reconfigureFeatures, setReconfigureFeatures] = React.useState(() => getDefaultCategoryLandingFeatureValues())

  const detailQuery = useQuery({
    queryKey: ['category-landing-page', id],
    queryFn: async () => {
      const res = await api.get(`/category-landing-pages/me/${id}`, { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    enabled: !!id,
    refetchOnWindowFocus: false,
  })

  const templatesQuery = useQuery({
    queryKey: ['category-landing-templates', detailQuery.data?.category],
    queryFn: async () => {
      const category = detailQuery.data?.category || ''
      const res = await api.get(`/category-landing-templates?category=${encodeURIComponent(category)}`, { __silent: true })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    enabled: !!detailQuery.data?.category,
    refetchOnWindowFocus: false,
  })
  const savedTemplates = React.useMemo(() => {
    const raw = templatesQuery.data
    return Array.isArray(raw) ? raw : []
  }, [templatesQuery.data])

  /** 드롭다운에 올라가는 값: API가 내려준 저장 템플릿 id만 (코드 프리셋 default/preset:* 제외) */
  const templatePickerValue = React.useMemo(() => {
    const sid = selectedTemplateId == null ? '' : String(selectedTemplateId)
    if (!sid) return ''
    return savedTemplates.some((t) => String(t.id) === sid) ? sid : ''
  }, [savedTemplates, selectedTemplateId])

  const resolvedTemplateIdForSave = React.useMemo(() => {
    const sid = selectedTemplateId == null ? '' : String(selectedTemplateId)
    if (sid && savedTemplates.some((t) => String(t.id) === sid)) {
      return sid
    }
    if (sid === 'default' || sid.startsWith('preset:')) {
      return sid
    }
    return detailQuery.data?.templateId || 'default'
  }, [savedTemplates, selectedTemplateId, detailQuery.data?.templateId])

  const selectedSavedTemplate = React.useMemo(() => {
    const sid = templatePickerValue == null ? '' : String(templatePickerValue)
    return savedTemplates.find((t) => String(t.id) === sid) || null
  }, [savedTemplates, templatePickerValue])
  const currentPageTemplate = React.useMemo(() => {
    const pageTid = detailQuery.data?.templateId
    if (!pageTid || pageTid === 'default' || String(pageTid).startsWith('preset:')) return null
    return savedTemplates.find((t) => String(t.id) === String(pageTid)) || null
  }, [detailQuery.data?.templateId, savedTemplates])
  const currentPageTemplateHint = React.useMemo(() => {
    const pageTid = detailQuery.data?.templateId
    if (!pageTid) return null
    if (pageTid === 'default' || String(pageTid).startsWith('preset:')) {
      return getCodeTemplatePresetHint(String(pageTid).replace(/^preset:/, '')) || {
        title: '기본 코드 템플릿',
        description: '코드 기반 기본 템플릿이 현재 페이지에 반영되어 있습니다.',
      }
    }
    return null
  }, [detailQuery.data?.templateId])
  const defaultHeroImage = React.useMemo(
    () => getCategoryLandingHeroImage(detailQuery.data?.category),
    [detailQuery.data?.category],
  )
  const heroImageOptions = React.useMemo(
    () => getCategoryLandingHeroImageOptions(detailQuery.data?.category),
    [detailQuery.data?.category],
  )

  const layoutOptions = React.useMemo(
    () => getCategoryLandingLayoutOptions(detailQuery.data?.category) || [],
    [detailQuery.data?.category],
  )

  React.useEffect(() => {
    const d = detailQuery.data
    if (!d) return
    setHeroTitle(d.heroTitle || '')
    setHeroSubtitle(d.heroSubtitle || '')
    setHeroImageFileId(d.heroImageFileId || '')
    setHeroVideoFileId(d.heroVideoFileId || '')
    setContentSections(parseJsonArray(d.contentSections, []))
    setContentSectionsJson(prettyJsonValue(d.contentSections || '[]'))
    setFormEnabled(Boolean(d.formEnabled))
    setFormTitle(d.formTitle || '상담 신청')
    setFormFields(parseJsonArray(d.formFields, createCategoryLandingDefaultFormFields({ includeMessage: true })))
    setFormFieldsJson(prettyJsonValue(d.formFields || '[]'))
    setSurveyEnabled(Boolean(d.surveyEnabled))
    setSurveyCustomFields(parseJsonArray(d.surveyCustomFields, []))
    setSurveyCustomFieldsJson(prettyJsonValue(d.surveyCustomFields || '[]'))
    const normalizedConfig = normalizeCategoryLandingLayoutConfig(parseJsonObject(d.customConfig || '{}', {}))
    setCustomConfigObj(normalizedConfig)
    setCustomConfigJson(prettyJsonValue(normalizedConfig))
    setEnabled(Boolean(d.enabled))
    setSelectedTemplateId(d.templateId || 'default')
    setAdReviewNotice(typeof d.adReviewNotice === 'string' ? d.adReviewNotice : '')

    const cfg = normalizeCategoryLandingLayoutConfig(parseJsonObject(d.customConfig || '{}'))
    const stats = Array.isArray(cfg?.introStats) ? cfg.introStats : []
    setKpiRetentionRate(String(stats.find((x) => x?.key === 'retentionRate')?.value || ''))
    setKpiConsultCount(String(stats.find((x) => x?.key === 'consultCount')?.value || ''))
    setKpiResponseRate(String(stats.find((x) => x?.key === 'responseRate')?.value || ''))
    setKpiSatisfaction(String(stats.find((x) => x?.key === 'satisfaction')?.value || ''))
  }, [detailQuery.data])

  const editorState = useCategoryLandingEditorState({
    jsonMode: editMode === 'advanced' && advancedEditorMode === 'json',
    fields: [
      { key: 'contentSections', label: '본문 섹션', kind: 'array', raw: contentSectionsJson, setRaw: setContentSectionsJson, fallback: [], value: contentSections, setValue: setContentSections },
      { key: 'formFields', label: '상담 폼 필드', kind: 'array', raw: formFieldsJson, setRaw: setFormFieldsJson, fallback: createCategoryLandingDefaultFormFields({ includeMessage: true }), value: formFields, setValue: setFormFields },
      { key: 'surveyCustomFields', label: '설문 필드', kind: 'array', raw: surveyCustomFieldsJson, setRaw: setSurveyCustomFieldsJson, fallback: [], value: surveyCustomFields, setValue: setSurveyCustomFields },
      { key: 'customConfig', label: '추가 설정', kind: 'object', raw: customConfigJson, setRaw: setCustomConfigJson, fallback: {}, value: customConfigObj, setValue: setCustomConfigObj },
    ],
  })

  const contentSectionsJsonParsed = editorState.parsedFields.contentSections
  const formFieldsJsonParsed = editorState.parsedFields.formFields
  const surveyCustomFieldsJsonParsed = editorState.parsedFields.surveyCustomFields
  const customConfigJsonParsed = editorState.parsedFields.customConfig

  const switchAdvancedEditorMode = (mode) => {
    if (mode === 'block') {
      try {
        editorState.applyParsedToValues()
      } catch (e) {
        try { window?.toast?.show?.(e?.message || 'JSON 형식을 확인해 주세요.', 'error') } catch { }
        return
      }
    }
    setAdvancedEditorMode(mode)
  }

  const getEditorDataForSave = () => {
    if (editMode === 'advanced' && advancedEditorMode === 'json') {
      editorState.validateJsonFields()
      return {
        contentSectionsValue: editorState.getResolvedValue('contentSections'),
        formFieldsValue: editorState.getResolvedValue('formFields'),
        surveyCustomFieldsValue: editorState.getResolvedValue('surveyCustomFields'),
        customConfigValue: editorState.getResolvedValue('customConfig'),
      }
    }
    return {
      contentSectionsValue: contentSections,
      formFieldsValue: formFields,
      surveyCustomFieldsValue: surveyCustomFields,
      customConfigValue: customConfigObj,
    }
  }

  const saveMutation = useMutation({
    mutationFn: async (noticeArg) => {
      const noticeTrim = String(noticeArg ?? adReviewNotice ?? '').trim()
      if (!noticeTrim) {
        const err = new Error('하단 광고 심의필 문구를 입력해야 저장할 수 있습니다.')
        err.code = 'AD_REVIEW_REQUIRED'
        throw err
      }
      const { contentSectionsValue, formFieldsValue, surveyCustomFieldsValue, customConfigValue } = getEditorDataForSave()
      const nextCustomConfig = { ...(customConfigValue || {}) }
      const pageData = detailQuery.data

      if (pageData?.category === 'PERSONAL_INTRO') {
        nextCustomConfig.introStats = [
          { key: 'retentionRate', label: '계약 유지율', value: String(kpiRetentionRate || '') },
          { key: 'consultCount', label: '상담 건수', value: String(kpiConsultCount || '') },
          { key: 'responseRate', label: '상담 답변율', value: String(kpiResponseRate || '') },
          { key: 'satisfaction', label: '고객 만족도', value: String(kpiSatisfaction || '') },
        ].filter((item) => item.value.trim())
      }

      const body = {
        enabled,
        templateId: resolvedTemplateIdForSave,
        heroTitle,
        heroSubtitle,
        heroImageFileId,
        heroVideoFileId,
        contentSections: JSON.stringify(contentSectionsValue || []),
        formEnabled,
        formTitle,
        formFields: JSON.stringify(formFieldsValue || []),
        surveyEnabled,
        surveyCustomFields: JSON.stringify(surveyCustomFieldsValue || []),
        customConfig: JSON.stringify(nextCustomConfig),
        adReviewNotice: noticeTrim,
      }
      const res = await api.put(`/category-landing-pages/me/${id}`, body)
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: () => {
      try { window?.toast?.show?.('저장되었습니다', 'success') } catch { }
      setSaveReviewOpen(false)
      detailQuery.refetch()
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '저장 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const applyTemplateMutation = useMutation({
    mutationFn: async (overwrite = false) => {
      if (!templatePickerValue) {
        const err = new Error('저장된 템플릿을 먼저 선택해 주세요.')
        err.code = 'NO_TEMPLATE'
        throw err
      }
      const res = await api.post(`/category-landing-pages/me/${id}/apply-template`, {
        templateId: templatePickerValue,
        overwrite,
      })
      return (res?.data && res.data.data !== undefined) ? res.data.data : res.data
    },
    onSuccess: () => {
      try { window?.toast?.show?.('템플릿이 적용되었습니다', 'success') } catch { }
      detailQuery.refetch()
    },
    onError: (e) => {
      const msg = e?.response?.data?.message || e?.message || '템플릿 적용 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    }
  })

  const getCategoryLandingComplianceText = useCallback(() => {
    const blocks = [
      heroTitle,
      heroSubtitle,
      formTitle,
      kpiRetentionRate,
      kpiConsultCount,
      kpiResponseRate,
      kpiSatisfaction,
      collectMarketingStrings(contentSections, 2500),
      collectMarketingStrings(customConfigObj, 1200),
      collectMarketingStrings(surveyCustomFields, 800),
    ]
    return blocks
      .filter((s) => String(s || '').trim())
      .join('\n')
      .slice(0, 4000)
  }, [
    heroTitle,
    heroSubtitle,
    formTitle,
    kpiRetentionRate,
    kpiConsultCount,
    kpiResponseRate,
    kpiSatisfaction,
    contentSections,
    customConfigObj,
    surveyCustomFields,
  ])

  const reconfigurePreviewState = React.useMemo(() => {
    const pageData = detailQuery.data
    if (!pageData) return null
    return buildCategoryLandingReconfigureState({
      category: pageData.category,
      purposeId: reconfigurePurpose,
      features: reconfigureFeatures,
      current: {
        heroTitle,
        heroSubtitle,
        heroImageFileId,
        heroVideoFileId,
        formTitle,
        contentSections,
        formFields,
        surveyCustomFields,
        customConfig: customConfigObj,
      },
    })
  }, [
    contentSections,
    customConfigObj,
    detailQuery.data,
    formFields,
    formTitle,
    heroImageFileId,
    heroSubtitle,
    heroTitle,
    heroVideoFileId,
    reconfigureFeatures,
    reconfigurePurpose,
    surveyCustomFields,
  ])

  const reconfigureWritingItems = React.useMemo(
    () => getCategoryLandingWritingItems(reconfigurePreviewState || {}),
    [reconfigurePreviewState],
  )

  const reconfigureSelectedFeatureLabels = React.useMemo(
    () => CATEGORY_LANDING_RECONFIGURE_FEATURES
      .filter((feature) => feature.required || reconfigureFeatures[feature.id] === true)
      .map((feature) => feature.label),
    [reconfigureFeatures],
  )

  React.useEffect(() => {
    if (autoReconfigureOpenedRef.current) return
    if (searchParams.get('reconfigure') !== '1') return
    const pageData = detailQuery.data
    if (!pageData) return

    autoReconfigureOpenedRef.current = true
    const purpose = getCategoryPurposeFromTemplate(selectedTemplateId || pageData.templateId)
    setReconfigurePurpose(purpose)
    setReconfigureFeatures(getDefaultCategoryLandingFeatureValues({
      contentSections: parseJsonArray(pageData.contentSections, []),
      formEnabled: Boolean(pageData.formEnabled),
      surveyEnabled: Boolean(pageData.surveyEnabled),
      adReviewNotice: typeof pageData.adReviewNotice === 'string' ? pageData.adReviewNotice : '',
    }, purpose))
    setReconfigureOpen(true)
    const nextParams = new URLSearchParams(searchParams)
    nextParams.delete('reconfigure')
    setSearchParams(nextParams, { replace: true })
  }, [detailQuery.data, searchParams, selectedTemplateId, setSearchParams])

  if (detailQuery.isLoading) return <Loading />
  if (detailQuery.isError) return <InlineError message={detailQuery.error?.message || 'Load failed'} />

  const d = detailQuery.data
  if (!d) return <InlineError message="No data" />
  const currentCategoryLabel = getCategoryLabel(d.category)
  const previewCustomConfig = editMode === 'advanced' && advancedEditorMode === 'json' && !customConfigJsonParsed.error
    ? normalizeCategoryLandingLayoutConfig(customConfigJsonParsed.value)
    : normalizeCategoryLandingLayoutConfig(customConfigObj)
  const currentLayoutKey = previewCustomConfig?.templateStyle || 'default'
  const currentLayoutEditableFields = getCategoryLandingLayoutEditableFields(currentLayoutKey) || []

  const updateLayoutConfig = (key, value) => {
    const normalized = normalizeCategoryLandingLayoutConfig({ ...(customConfigObj || {}), [key]: value })
    setCustomConfigObj(normalized)
    setCustomConfigJson(prettyJsonValue(normalized))
  }

  const changeLayout = (layoutKey) => {
    const normalized = applyCategoryLandingLayout(layoutKey, customConfigObj || {})
    setCustomConfigObj(normalized)
    setCustomConfigJson(prettyJsonValue(normalized))
  }

  const updateSection = (idx, key, value) => {
    setContentSections((prev) => prev.map((s, i) => (i === idx ? { ...s, [key]: value } : s)))
  }

  const uploadMedia = async (file, target, sectionIdx = -1) => {
    if (!file) return
    if (file.size > 10 * 1024 * 1024) {
      try { window?.toast?.show?.('파일 크기는 10MB 이하만 가능합니다.', 'error') } catch { }
      return
    }
    setUploadingTarget(target)
    try {
      const form = new FormData()
      form.append('file', file)
      const res = await api.post('/files/upload/public-landing-image', form)
      const fileId = res?.data?.data?.fileId || res?.data?.fileId
      if (!fileId) throw new Error('파일 식별자를 받지 못했습니다.')
      if (target === 'heroImage') {
        setHeroImageFileId(fileId)
      } else if (target === 'heroVideo') {
        setHeroVideoFileId(fileId)
      } else if (target === 'sectionImage' && sectionIdx >= 0) {
        updateSection(sectionIdx, 'fileId', fileId)
      } else if (target === 'sectionVideo' && sectionIdx >= 0) {
        updateSection(sectionIdx, 'url', fileId)
      }
      try { window?.toast?.show?.('업로드되었습니다.', 'success') } catch { }
    } catch (e) {
      const msg = e?.response?.data?.message || e?.message || '업로드 실패'
      try { window?.toast?.show?.(msg, 'error') } catch { }
    } finally {
      setUploadingTarget('')
    }
  }

  const browserOrigin = typeof window !== 'undefined' ? window.location.origin : ''
  const publicLandingUrl = d?.code ? `${browserOrigin}/c/${d.code}` : ''
  const sharedLandingUrl = d?.code ? categoryLandingOgShareUrl(d.code, d?.ogVersion || d?.updatedAt || '') : ''
  const canShare = Boolean(enabled && publicLandingUrl && sharedLandingUrl)
  /** 공개 중이면 실제 공개 URL, 아니면 초안 미리보기(편집용) */
  const previewHref = canShare && d?.code ? `/c/${d.code}` : `/category-landing-pages/${d.id}/preview`
  const utilityButtonClass = 'inline-flex h-9 items-center justify-center rounded-xl border border-slate-200 bg-white px-3.5 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
  const primaryButtonClass = 'inline-flex h-10 items-center justify-center rounded-xl px-4 text-sm font-semibold text-white disabled:opacity-60'
  const shareButtonClass = 'inline-flex h-[42px] min-w-[96px] items-center justify-center whitespace-nowrap rounded-lg border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50'
  const copyToClipboard = async (text, successMessage = '복사되었습니다.') => {
    if (!text) {
      try { window?.toast?.show?.('복사할 링크가 없습니다.', 'error') } catch { }
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      try { window?.toast?.show?.(successMessage, 'success') } catch { }
    } catch {
      try { window?.toast?.show?.('클립보드 복사에 실패했습니다.', 'error') } catch { }
    }
  }

  const openReconfigure = () => {
    const purpose = getCategoryPurposeFromTemplate(selectedTemplateId || d?.templateId)
    setReconfigurePurpose(purpose)
    setReconfigureFeatures(getDefaultCategoryLandingFeatureValues({
      contentSections,
      formEnabled,
      surveyEnabled,
      adReviewNotice,
    }, purpose))
    setReconfigureOpen(true)
  }

  const applyReconfigure = () => {
    const next = reconfigurePreviewState || buildCategoryLandingReconfigureState({
      category: d.category,
      purposeId: reconfigurePurpose,
      features: reconfigureFeatures,
      current: {
        heroTitle,
        heroSubtitle,
        heroImageFileId,
        heroVideoFileId,
        formTitle,
        contentSections,
        formFields,
        surveyCustomFields,
        customConfig: customConfigObj,
      },
    })
    setSelectedTemplateId(next.templateId)
    setHeroTitle(next.heroTitle)
    setHeroSubtitle(next.heroSubtitle)
    setHeroImageFileId(next.heroImageFileId)
    setHeroVideoFileId(next.heroVideoFileId)
    setFormTitle(next.formTitle)
    setContentSections(next.contentSections)
    setContentSectionsJson(prettyJsonValue(next.contentSections))
    setFormEnabled(next.formEnabled)
    setFormFields(next.formFields)
    setFormFieldsJson(prettyJsonValue(next.formFields))
    setSurveyEnabled(next.surveyEnabled)
    setSurveyCustomFields(next.surveyCustomFields)
    setSurveyCustomFieldsJson(prettyJsonValue(next.surveyCustomFields))
    setCustomConfigObj(next.customConfig)
    setCustomConfigJson(prettyJsonValue(next.customConfig))
    setEditMode((next.contentSections || []).length > 0 || next.formEnabled || next.surveyEnabled ? 'advanced' : 'standard')
    setAdvancedEditorMode('block')
    setReconfigureOpen(false)
    try { window?.toast?.show?.('홍보용페이지 구성을 적용했습니다. 저장하면 고객에게 반영됩니다.', 'success') } catch { }
  }

  return (
    <div className="mx-auto max-w-[1600px] space-y-4">
      <div className="rounded-3xl border border-slate-200 bg-white px-5 py-3.5">
        <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
          <div className="min-w-0">
            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-400">Category Landing Builder</div>
            <div className="mt-1 flex flex-wrap items-center gap-2">
              <h1 className="text-lg font-bold text-slate-900">홍보용페이지 편집</h1>
              <span className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${enabled ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'}`}>
                {enabled ? '공개 중' : '비공개'}
              </span>
              <span className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-medium text-slate-600">
                {currentCategoryLabel}
              </span>
            </div>
            <div className="mt-1 text-xs text-slate-500">
              공개 주소: /c/{d.code}
            </div>
            <div className="mt-4 flex flex-wrap items-center gap-2">
              <ShareLinkActions
                items={[
                  { id: 'public', label: '공개 링크', url: publicLandingUrl },
                  { id: 'kakao-share', label: '카카오 공유용 링크', url: sharedLandingUrl },
                ]}
                disabled={!canShare}
                disabledMessage="공개 상태에서만 링크를 공유할 수 있습니다."
                onCopy={copyToClipboard}
                containerClassName="contents"
                buttonClassName={shareButtonClass}
                showQrButton={false}
              />
              <a
                className={shareButtonClass}
                href={previewHref}
                target="_blank"
                rel="noreferrer"
              >
                미리보기
                <span aria-hidden="true" className="ml-1 text-xs">
                  ↗
                </span>
              </a>
              <ShareLinkActions
                items={[
                  { id: 'public', label: '공개 링크', url: publicLandingUrl },
                  { id: 'kakao-share', label: '카카오 공유용 링크', url: sharedLandingUrl },
                ]}
                disabled={!canShare}
                disabledMessage="공개 상태에서만 링크를 공유할 수 있습니다."
                onCopy={copyToClipboard}
                containerClassName="contents"
                buttonClassName={shareButtonClass}
                showCopyButtons={false}
                qrButtonLabel="QR 보기"
              />
            </div>
          </div>
          <div className="flex w-full max-w-[420px] flex-col gap-2 xl:items-end">
            <div className="flex flex-wrap gap-2 xl:justify-end">
              <Link className={utilityButtonClass} to="/category-landing-pages">목록</Link>
              <button
                type="button"
                className="inline-flex h-9 items-center justify-center rounded-xl border border-orange-200 bg-orange-50 px-3.5 text-sm font-semibold text-orange-800 transition hover:bg-orange-100"
                onClick={openReconfigure}
              >
                다시 처음부터 구성하기
              </button>
              <button
                type="button"
                className={`${primaryButtonClass} bg-brand-navy hover:opacity-90`}
                disabled={saveMutation.isLoading}
                onClick={() => {
                  setSaveReviewNoticeDraft(adReviewNotice || '')
                  setSaveReviewOpen(true)
                }}
              >
                {saveMutation.isLoading ? '저장 중...' : '저장'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 편집 방식 선택 */}
      <div className="rounded-xl border p-4 bg-white">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="text-sm font-semibold">편집 방식</div>
            <div className="text-xs text-slate-500 mt-1">
              {editMode === 'standard'
                ? '템플릿과 상단 문구 중심으로 간단히 작성합니다.'
                : '본문, 폼, 설문까지 상세하게 작성합니다.'}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              className="rounded-xl border border-orange-200 bg-orange-50 px-3 py-2 text-sm font-semibold text-orange-800 hover:bg-orange-100"
              onClick={openReconfigure}
            >
              다시 처음부터 구성하기
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                editMode === 'standard'
                  ? 'border border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                  : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
              onClick={() => setEditMode('standard')}
            >
              기본편집
            </button>
            <button
              type="button"
              className={`rounded-xl px-3 py-2 text-sm font-semibold transition-colors ${
                editMode === 'advanced'
                  ? 'border border-brand-navy bg-sky-50 text-brand-navy shadow-sm ring-1 ring-brand-navy/10'
                  : 'border border-slate-200 bg-white text-slate-700 hover:bg-slate-50'
              }`}
              onClick={() => {
                setEditMode('advanced')
                setAdvancedEditorMode('block')
              }}
            >
              고급편집
            </button>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-4">
          {/* 템플릿 선택 및 적용 */}
          <div className="rounded-xl border p-4 space-y-3 bg-blue-50">
            <div className="text-sm font-semibold text-slate-700">템플릿 관리</div>
            <div className="space-y-2">
              <div>
                <label className="block text-xs text-slate-600 mb-1">템플릿 선택</label>
                <select
                  className="w-full rounded border px-3 py-2 text-sm bg-white"
                  value={templatePickerValue}
                  onChange={(e) => setSelectedTemplateId(e.target.value)}
                  disabled={templatesQuery.isLoading}
                >
                  <option value="">저장된 템플릿 선택…</option>
                  {savedTemplates.map((t) => (
                    <option key={t.id} value={String(t.id)}>{t.name || `템플릿 #${t.id}`}</option>
                  ))}
                </select>
              </div>
              {/* <div className="text-xs text-slate-500 bg-white p-2 rounded">
                {templateHelpText}
              </div> */}
              {selectedSavedTemplate && (
                <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 rounded-xl border bg-white p-3">
                  <TemplateThumb
                    src={selectedSavedTemplate.heroImageFileId}
                    alt={selectedSavedTemplate.name || '선택 템플릿'}
                    className="h-24 w-full bg-slate-100"
                  />
                  <div className="min-w-0 space-y-1">
                    <div className="flex items-center gap-2">
                      <span className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-semibold text-sky-700">적용 대상</span>
                      <span className="text-[11px] text-slate-400">템플릿 #{selectedSavedTemplate.id}</span>
                    </div>
                    <div className="truncate text-sm font-semibold text-slate-800">{selectedSavedTemplate.name || `템플릿 #${selectedSavedTemplate.id}`}</div>
                    <div className="line-clamp-2 text-xs leading-5 text-slate-500">{selectedSavedTemplate.heroTitle || selectedSavedTemplate.heroSubtitle || '템플릿 설명이 없습니다.'}</div>
                  </div>
                </div>
              )}
              {editMode === 'standard' ? (
                <>
                  <button
                    type="button"
                    className="w-full rounded bg-brand-navy text-white px-3 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    onClick={() => {
                      if (window.confirm('템플릿을 적용하면 현재 페이지 내용이 선택한 템플릿으로 교체됩니다. 계속하시겠습니까?')) {
                        setOverwriteTemplate(true)
                        applyTemplateMutation.mutate(true)
                      }
                    }}
                    disabled={applyTemplateMutation.isLoading || templatesQuery.isLoading || !templatePickerValue}
                  >
                    {applyTemplateMutation.isLoading ? '적용 중...' : '템플릿 적용하기'}
                  </button>
                  {/* <div className="text-xs text-slate-500 bg-white p-2 rounded">
                    <strong>표준 모드:</strong> 저장된 템플릿을 고른 뒤 적용하면 페이지가 구성됩니다. 기본 코드 템플릿(1~5형)은 운영 관리 메뉴에서만 불러올 수 있습니다.
                  </div> */}
                </>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="overwrite-template"
                      checked={overwriteTemplate}
                      onChange={(e) => setOverwriteTemplate(e.target.checked)}
                      className="rounded"
                    />
                    <label htmlFor="overwrite-template" className="text-xs text-slate-600 cursor-pointer">
                      기존 내용 덮어쓰기 (체크 시 모든 내용이 템플릿으로 교체됩니다)
                    </label>
                  </div>
                  <button
                    type="button"
                    className="w-full rounded bg-brand-navy text-white px-3 py-2 text-sm font-semibold hover:opacity-90 disabled:opacity-50"
                    onClick={() => {
                      if (window.confirm(overwriteTemplate 
                        ? '템플릿을 적용하면 현재 편집 중인 모든 내용이 덮어씌워집니다. 계속하시겠습니까?'
                        : '템플릿을 적용하면 비어있는 필드만 채워집니다. 계속하시겠습니까?')) {
                        applyTemplateMutation.mutate(overwriteTemplate)
                      }
                    }}
                    disabled={applyTemplateMutation.isLoading || templatesQuery.isLoading || !templatePickerValue}
                  >
                    {applyTemplateMutation.isLoading ? '적용 중...' : '템플릿 적용하기'}
                  </button>
                </>
              )}
              <div className="text-xs text-slate-500">
                현재 템플릿: {(() => {
                  const pageTid = detailQuery.data?.templateId
                  const sid = selectedTemplateId == null ? '' : String(selectedTemplateId)
                  const byPick = sid && savedTemplates.find((t) => String(t.id) === sid)
                  if (byPick) return byPick.name
                  if (pageTid === 'default' || (pageTid && String(pageTid).startsWith('preset:'))) {
                    return '기본 코드 템플릿'
                  }
                  const byPage = pageTid && savedTemplates.find((t) => String(t.id) === String(pageTid))
                  if (byPage) return byPage.name
                  return pageTid ? `참조: ${pageTid}` : '알 수 없음'
                })()}
              </div>
              {currentPageTemplate && (
                <div className="grid grid-cols-[112px_minmax(0,1fr)] gap-3 rounded-xl border bg-white p-3">
                  <TemplateThumb
                    src={currentPageTemplate.heroImageFileId}
                    alt={currentPageTemplate.name || '현재 템플릿'}
                    className="h-24 w-full bg-slate-100"
                  />
                  <div className="min-w-0 space-y-1">
                    <div className="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-600 inline-flex w-fit">현재 참조 중</div>
                    <div className="truncate text-sm font-semibold text-slate-800">{currentPageTemplate.name || `템플릿 #${currentPageTemplate.id}`}</div>
                    <div className="line-clamp-2 text-xs leading-5 text-slate-500">{currentPageTemplate.heroTitle || currentPageTemplate.heroSubtitle || '템플릿 설명이 없습니다.'}</div>
                  </div>
                </div>
              )}
              {!currentPageTemplate && currentPageTemplateHint && (
                <div className="rounded-xl border bg-white p-3 text-xs leading-5 text-slate-500">
                  <strong className="text-slate-700">{currentPageTemplateHint.title}</strong> {currentPageTemplateHint.description}
                </div>
              )}
            </div>
          </div>

          <div className="rounded-xl border p-4 space-y-4 bg-white">
            <div className="flex items-center justify-between">
              <label className="text-sm font-semibold">페이지 공개</label>
              <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} />
            </div>
            <div>
              <label className="block text-sm text-slate-600">상단 제목</label>
              <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[72px]" value={heroTitle} onChange={(e) => setHeroTitle(e.target.value)} maxLength={200} rows={3} />
              <div className="mt-1 text-xs text-slate-500">엔터로 줄을 나누면 공개 페이지에도 동일하게 줄바꿈됩니다.</div>
            </div>
            <div>
              <label className="block text-sm text-slate-600">상단 설명</label>
              <textarea className="mt-1 w-full rounded border px-3 py-2 min-h-[90px]" value={heroSubtitle} onChange={(e) => setHeroSubtitle(e.target.value)} maxLength={300} />
              {editMode === 'advanced' && (
                <div className="mt-1 text-xs text-slate-500">권장: 1-2줄 (최대 300자). 짧고 임팩트 있는 문구를 사용하세요.</div>
              )}
            </div>
            {editMode === 'advanced' && (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600 font-semibold">
                      상단 배경 이미지 <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-1 text-xs text-slate-500 mb-1">
                      홍보용페이지의 첫인상을 결정하는 중요한 이미지입니다. 인포그래픽 또는 배경 이미지를 권장합니다. 권장 해상도: 가로 1920px 이상(16:9~3:2), 최소 가로 1200px. 레이아웃에 따라 상·하가 잘릴 수 있습니다.
                    </div>
                    <div className="mt-1 flex gap-2">
                      <input className="w-full rounded border px-3 py-2" value={heroImageFileId} onChange={(e) => setHeroImageFileId(e.target.value)} placeholder="파일 ID 또는 파일 선택" />
                      <label className="rounded border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 bg-blue-50 font-semibold">
                        파일선택
                        <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0], 'heroImage')} />
                      </label>
                    </div>
                    {defaultHeroImage ? (
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          className="rounded border border-sky-200 bg-sky-50 px-3 py-1.5 text-xs font-semibold text-sky-700 hover:bg-sky-100"
                          onClick={() => setHeroImageFileId(defaultHeroImage)}
                        >
                          카테고리 기본 이미지 적용
                        </button>
                        <span className="text-xs text-slate-400">직접 업로드한 이미지가 마음에 들지 않으면 기본 이미지로 되돌릴 수 있습니다.</span>
                      </div>
                    ) : null}
                    {heroImageOptions.length > 0 ? (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50 p-3">
                        <div className="mb-2 text-xs font-semibold text-slate-600">기본 제공 이미지 선택</div>
                        <div className="grid max-h-96 grid-cols-1 gap-2 overflow-y-auto pr-1 sm:grid-cols-2">
                          {heroImageOptions.map((option) => {
                            const active = heroImageFileId === option.fileId
                            return (
                              <button
                                key={option.fileId}
                                type="button"
                                className={`overflow-hidden rounded-xl border bg-white text-left transition ${
                                  active
                                    ? 'border-brand-navy ring-2 ring-brand-navy/15'
                                    : 'border-slate-200 hover:border-sky-200 hover:bg-sky-50/40'
                                }`}
                                onClick={() => setHeroImageFileId(option.fileId)}
                              >
                                <TemplateThumb
                                  src={option.fileId}
                                  alt={option.label}
                                  className="h-24 w-full rounded-none bg-slate-100"
                                />
                                <div className="px-3 py-2">
                                  <div className="text-xs font-bold text-slate-800">{option.label}</div>
                                  {option.description ? (
                                    <div className="mt-0.5 text-[11px] leading-4 text-slate-500">{option.description}</div>
                                  ) : null}
                                </div>
                              </button>
                            )
                          })}
                        </div>
                      </div>
                    ) : null}
                  </div>
                  <div>
                    <label className="block text-sm text-slate-600">상단 영상 파일 ID</label>
                    <div className="mt-1 flex gap-2">
                      <input className="w-full rounded border px-3 py-2" value={heroVideoFileId} onChange={(e) => setHeroVideoFileId(e.target.value)} />
                      <label className="rounded border px-3 py-2 text-sm cursor-pointer hover:bg-slate-50">
                        파일선택
                        <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadMedia(e.target.files?.[0], 'heroVideo')} />
                      </label>
                    </div>
                {heroImageFileId ? (
                  <div className="rounded-xl border bg-slate-50 p-3">
                    <div className="mb-2 text-xs font-semibold text-slate-500">현재 상단 이미지</div>
                    <TemplateThumb src={heroImageFileId} alt="현재 상단 이미지" className="max-h-56 w-full bg-slate-100" />
                  </div>
                ) : null}
                  </div>
                </div>
                {uploadingTarget && <div className="text-xs text-slate-500">업로드 중...</div>}
              </>
            )}
          </div>

          {editMode === 'advanced' && (
            <CategoryLandingEditorModeToggle
              title="고급 편집 방식"
              description="같은 데이터를 블록 편집 또는 JSON 편집으로 수정할 수 있습니다."
              mode={advancedEditorMode}
              onChange={switchAdvancedEditorMode}
              options={[
                { value: 'block', label: '블록 편집' },
                { value: 'json', label: 'JSON 편집' },
              ]}
            />
          )}

          {editMode === 'advanced' && advancedEditorMode === 'block' && (
            <CategoryLandingSectionBlockEditor
              sections={contentSections}
              onChange={setContentSections}
              title="본문 섹션"
              addButtonLabel="섹션 추가"
              useRichLabels
              allowMediaUpload
              onUploadMedia={uploadMedia}
            />
          )}

          {editMode === 'advanced' && advancedEditorMode === 'block' && (
            <>
              <div className="rounded-xl border p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">상담 폼 사용</label>
                  <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
                </div>
                <div>
                  <label className="block text-sm text-slate-600">상담 폼 제목</label>
                  <input className="mt-1 w-full rounded border px-3 py-2" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} maxLength={200} />
                </div>
                <CategoryLandingFieldListEditor
                  title="상담 폼 필드"
                  items={formFields}
                  onChange={setFormFields}
                  keyName="name"
                  keyLabel="키(name)"
                  addLabel="필드 추가"
                  createItem={(items) => ({ name: `field_${items.length + 1}`, label: '', required: false, type: 'text' })}
                  typeOptions={[
                    { value: 'text', label: '텍스트' },
                    { value: 'tel', label: '전화번호' },
                    { value: 'textarea', label: '긴글' },
                  ]}
                  showPlaceholder={false}
                  showOptions={false}
                />
              </div>

              <div className="rounded-xl border p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">설문 사용</label>
                  <input type="checkbox" checked={surveyEnabled} onChange={(e) => setSurveyEnabled(e.target.checked)} />
                </div>
                <CategoryLandingFieldListEditor
                  title="설문 문항"
                  items={surveyCustomFields}
                  onChange={setSurveyCustomFields}
                  keyName="id"
                  keyLabel="ID"
                  addLabel="문항 추가"
                  createItem={(items) => ({ id: `q_${items.length + 1}`, label: '', required: false, type: 'text' })}
                  typeOptions={[
                    { value: 'text', label: '텍스트' },
                    { value: 'textarea', label: '긴글' },
                  ]}
                  showPlaceholder={false}
                  showOptions={false}
                />
              </div>

              <div className="rounded-xl border p-4 space-y-4 bg-white">
                <div className="font-semibold text-sm">레이아웃 / 공통 스타일 설정</div>
                <div className="text-xs text-slate-500">레이아웃 구조는 고정되며, 공통 인터페이스만 수정할 수 있습니다.</div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm text-slate-600">레이아웃 템플릿</label>
                    <select className="mt-1 w-full rounded border px-3 py-2" value={currentLayoutKey} onChange={(e) => changeLayout(e.target.value)}>
                      {layoutOptions.map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </div>
                  {currentLayoutEditableFields.map((field) => (
                    <div key={field.key}>
                      <label className="block text-sm text-slate-600">{field.label}</label>
                      {field.type === 'number' ? (
                        <input
                          type="number"
                          className="mt-1 w-full rounded border px-3 py-2"
                          value={previewCustomConfig?.[field.key] ?? ''}
                          onChange={(e) => updateLayoutConfig(field.key, coerceLayoutFieldValue(field, e.target.value))}
                          placeholder={field.placeholder || ''}
                        />
                      ) : isCategoryLandingLayoutColorKey(field.key) ? (
                        <CategoryLandingLayoutColorField
                          value={previewCustomConfig?.[field.key] ?? ''}
                          onChange={(v) => updateLayoutConfig(field.key, coerceLayoutFieldValue(field, v))}
                          placeholder={field.placeholder || ''}
                        />
                      ) : (
                        <input
                          type="text"
                          className="mt-1 w-full rounded border px-3 py-2"
                          value={previewCustomConfig?.[field.key] ?? ''}
                          onChange={(e) => updateLayoutConfig(field.key, coerceLayoutFieldValue(field, e.target.value))}
                          placeholder={field.placeholder || ''}
                        />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {d.category === 'PERSONAL_INTRO' && (
                <div className="rounded-xl border p-4 space-y-4 bg-white">
                  <div className="font-semibold text-sm">본인소개 신뢰 지표 (선택)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-sm text-slate-600">계약 유지율</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiRetentionRate} onChange={(e) => setKpiRetentionRate(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">상담 건수</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiConsultCount} onChange={(e) => setKpiConsultCount(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">상담 답변율</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiResponseRate} onChange={(e) => setKpiResponseRate(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">고객 만족도</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiSatisfaction} onChange={(e) => setKpiSatisfaction(e.target.value)} /></div>
                  </div>
                </div>
              )}
            </>
          )}

          {editMode === 'advanced' && advancedEditorMode === 'json' && (
            <>
              <CategoryLandingJsonEditorCard
                title="본문 섹션(JSON)"
                value={contentSectionsJson}
                onChange={setContentSectionsJson}
                error={contentSectionsJsonParsed.error}
                minHeight={220}
              />

              <div className="rounded-xl border p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">상담 폼 사용</label>
                  <input type="checkbox" checked={formEnabled} onChange={(e) => setFormEnabled(e.target.checked)} />
                </div>
                <div>
                  <label className="block text-sm text-slate-600">상담 폼 제목</label>
                  <input className="mt-1 w-full rounded border px-3 py-2" value={formTitle} onChange={(e) => setFormTitle(e.target.value)} maxLength={200} />
                </div>
                <CategoryLandingJsonEditorCard
                  title="상담 폼 필드(JSON)"
                  value={formFieldsJson}
                  onChange={setFormFieldsJson}
                  error={formFieldsJsonParsed.error}
                  minHeight={180}
                />
              </div>

              <div className="rounded-xl border p-4 space-y-3 bg-white">
                <div className="flex items-center justify-between">
                  <label className="text-sm font-semibold">설문 사용</label>
                  <input type="checkbox" checked={surveyEnabled} onChange={(e) => setSurveyEnabled(e.target.checked)} />
                </div>
                <CategoryLandingJsonEditorCard
                  title="설문 문항(JSON)"
                  value={surveyCustomFieldsJson}
                  onChange={setSurveyCustomFieldsJson}
                  error={surveyCustomFieldsJsonParsed.error}
                  minHeight={180}
                />
              </div>

              <CategoryLandingJsonEditorCard
                title="추가 설정(JSON 읽기전용)"
                value={prettyJsonValue(previewCustomConfig)}
                onChange={() => {}}
                error=""
                minHeight={140}
                readOnly
              />

              {d.category === 'PERSONAL_INTRO' && (
                <div className="rounded-xl border p-4 space-y-4 bg-white">
                  <div className="font-semibold text-sm">본인소개 신뢰 지표 (선택)</div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div><label className="block text-sm text-slate-600">계약 유지율</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiRetentionRate} onChange={(e) => setKpiRetentionRate(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">상담 건수</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiConsultCount} onChange={(e) => setKpiConsultCount(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">상담 답변율</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiResponseRate} onChange={(e) => setKpiResponseRate(e.target.value)} /></div>
                    <div><label className="block text-sm text-slate-600">고객 만족도</label><input className="mt-1 w-full rounded border px-3 py-2" value={kpiSatisfaction} onChange={(e) => setKpiSatisfaction(e.target.value)} /></div>
                  </div>
                </div>
              )}
            </>
          )}

        </div>

      </div>

      <EasyReconfigureModal
        open={reconfigureOpen}
        title="홍보용페이지 다시 구성하기"
        description={`${currentCategoryLabel || '현재 카테고리'} 안에서 목표에 맞는 템플릿과 기능을 다시 정리합니다.`}
        purposeLabel="이 홍보용페이지의 목표는 무엇인가요?"
        purposes={CATEGORY_LANDING_RECONFIGURE_PURPOSES}
        purposeId={reconfigurePurpose}
        onPurposeIdChange={(value) => {
          setReconfigurePurpose(value)
          setReconfigureFeatures(getDefaultCategoryLandingFeatureValues({}, value, { preferCurrent: false }))
        }}
        features={CATEGORY_LANDING_RECONFIGURE_FEATURES}
        featureValues={reconfigureFeatures}
        onFeatureChange={(id, value) => setReconfigureFeatures((prev) => ({ ...prev, [id]: value }))}
        onApply={applyReconfigure}
        onClose={() => setReconfigureOpen(false)}
        applyLabel="작성 시작"
        note="새 홍보용페이지를 만들 때는 먼저 카테고리를 선택합니다. 이 화면에서는 현재 카테고리 안에서 구성을 다시 잡습니다."
        stepMode
        stepLabels={['목표 선택', '기능 선택', '작성 항목']}
        renderReview={() => (
          <section>
            <div className="text-sm font-bold text-slate-900">이 순서로 작성합니다</div>
            <div className="mt-3 overflow-hidden rounded-3xl border border-slate-200 bg-white">
              {reconfigureWritingItems.map((item, index) => (
                <div
                  key={item.id}
                  className={`flex gap-3 px-4 py-4 ${index === 0 ? '' : 'border-t border-slate-100'}`}
                >
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-sky-50 text-xs font-black text-brand-navy">
                    {index + 1}
                  </span>
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-slate-950">{item.label}</div>
                    <div className="mt-1 text-xs leading-5 text-slate-500">{item.description}</div>
                  </div>
                </div>
              ))}
            </div>
            {reconfigureSelectedFeatureLabels.length > 0 ? (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {reconfigureSelectedFeatureLabels.map((label) => (
                  <span
                    key={label}
                    className="rounded-full bg-slate-100 px-2.5 py-1 text-[11px] font-bold text-slate-700"
                  >
                    {label}
                  </span>
                ))}
              </div>
            ) : null}
          </section>
        )}
      />

      <CategoryLandingSaveReviewModal
        open={saveReviewOpen}
        onClose={() => {
          if (!saveMutation.isLoading) setSaveReviewOpen(false)
        }}
        noticeDraft={saveReviewNoticeDraft}
        onNoticeDraftChange={setSaveReviewNoticeDraft}
        getComplianceText={getCategoryLandingComplianceText}
        saving={saveMutation.isLoading}
        onConfirmSave={() => {
          const t = saveReviewNoticeDraft.trim()
          if (!t) {
            try { window?.toast?.show?.('하단 광고 심의필 문구를 입력해야 저장할 수 있습니다.', 'error') } catch { }
            return
          }
          setAdReviewNotice(t)
          saveMutation.mutate(t)
        }}
      />
    </div>
  )
}
